import { Api } from '@core/api';
import { injectTSDI } from '@core/tsdi';
import { TextField } from '@components/textfield';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import { Label } from '@components/ui/label';
import { Switch } from '@components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@components/ui/tabs';
import React from 'react';
import {
    CommunityLocationDto,
    CommunityLocationRequestDto,
    LocationGeometryType
} from '@api/ChallengeClient';
import { DrawnGeometry, LocationMap, LocationMapHandle } from './location-map';

interface Props {
    open: boolean;
    communityId: number;
    location?: CommunityLocationDto;
    onClose: () => void;
    onSaved: () => void;
}

export const LocationDialog: React.FC<Props> = ({
    open,
    communityId,
    location,
    onClose,
    onSaved
}) => {
    const { challengeClient } = injectTSDI(Api);
    const mapRef = React.useRef<LocationMapHandle>(null);

    const [name, setName] = React.useState('');
    const [geometryType, setGeometryType] =
        React.useState<LocationGeometryType>(LocationGeometryType.CIRCLE);
    const [geometry, setGeometry] = React.useState<DrawnGeometry | null>(null);
    // null = not editing → display the actual circle radius; a string = in-flight edit
    const [radiusDraft, setRadiusDraft] = React.useState<string | null>(null);
    const [restrictedForWorkoutRecording, setRestrictedForWorkoutRecording] =
        React.useState(true);
    const [recordedForArrivals, setRecordedForArrivals] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [busy, setBusy] = React.useState(false);

    // reset form whenever the dialog (re)opens
    React.useEffect(() => {
        if (open) {
            setName(location?.name ?? '');
            setGeometryType(
                location?.geometryType ?? LocationGeometryType.CIRCLE
            );
            setGeometry(null);
            setRadiusDraft(null);
            setRestrictedForWorkoutRecording(
                location?.restrictedForWorkoutRecording ?? true
            );
            setRecordedForArrivals(location?.recordedForArrivals ?? false);
            setError(null);
        }
    }, [open, location]);

    const radiusMeters =
        geometry?.geometryType === LocationGeometryType.CIRCLE
            ? geometry.circle.radiusMeters
            : undefined;

    const radiusValue =
        radiusDraft ??
        (radiusMeters !== undefined ? String(Math.round(radiusMeters)) : '');

    // On commit, push a valid radius to the map (creating a circle at the map
    // centre if none exists yet); always drop the draft so the field falls back
    // to the circle's real radius (restores it after an empty/invalid entry).
    const commitRadius = () => {
        const n = Number(radiusDraft);
        if (radiusDraft !== null && n > 0) {
            mapRef.current?.setCircleRadius(n);
        }
        setRadiusDraft(null);
    };

    const canSave = name.trim().length > 0 && geometry !== null && !busy;

    const save = async () => {
        if (!geometry) {
            return;
        }
        setBusy(true);
        setError(null);
        // Build the body from the drawn geometry's own type so the geometry type
        // and the populated shape can never disagree.
        const body: CommunityLocationRequestDto =
            geometry.geometryType === LocationGeometryType.CIRCLE
                ? {
                      name: name.trim(),
                      geometryType: LocationGeometryType.CIRCLE,
                      circle: geometry.circle,
                      restrictedForWorkoutRecording,
                      recordedForArrivals
                  }
                : {
                      name: name.trim(),
                      geometryType: LocationGeometryType.POLYGON,
                      polygon: geometry.polygon,
                      restrictedForWorkoutRecording,
                      recordedForArrivals
                  };
        try {
            if (location) {
                await challengeClient.support.communityLocationSupportControllerUpdate(
                    communityId,
                    location.id,
                    body
                );
            } else {
                await challengeClient.support.communityLocationSupportControllerCreate(
                    communityId,
                    body
                );
            }
            onSaved();
            onClose();
        } catch (e) {
            setError(
                e instanceof Error ? e.message : 'Failed to save location'
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
                <DialogHeader>
                    <DialogTitle>
                        {location ? 'Edit' : 'Create'} location
                    </DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-4">
                    {error && (
                        <Alert variant="destructive">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    <TextField
                        label="Name"
                        type="text"
                        required
                        value={name}
                        onChange={(value) => setName(String(value))}
                    />

                    <div className="flex flex-wrap gap-6">
                        <div className="flex items-center gap-2">
                            <Switch
                                id="location-restricted"
                                checked={restrictedForWorkoutRecording}
                                onCheckedChange={
                                    setRestrictedForWorkoutRecording
                                }
                            />
                            <Label htmlFor="location-restricted">
                                Restricted for workout recording
                            </Label>
                        </div>
                        <div className="flex items-center gap-2">
                            <Switch
                                id="location-arrivals"
                                checked={recordedForArrivals}
                                onCheckedChange={setRecordedForArrivals}
                            />
                            <Label htmlFor="location-arrivals">
                                Recorded for arrivals
                            </Label>
                        </div>
                    </div>

                    <Tabs
                        value={geometryType}
                        onValueChange={(value) => {
                            setGeometryType(value as LocationGeometryType);
                            setGeometry(null);
                        }}
                    >
                        <TabsList>
                            <TabsTrigger value={LocationGeometryType.POLYGON}>
                                Polygon
                            </TabsTrigger>
                            <TabsTrigger value={LocationGeometryType.CIRCLE}>
                                Circle
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>

                    {geometryType === LocationGeometryType.CIRCLE && (
                        <div className="flex max-w-xs flex-col gap-1.5">
                            <TextField
                                label="Radius (m)"
                                type="number"
                                value={radiusValue}
                                onChange={(value) =>
                                    setRadiusDraft(String(value))
                                }
                                onBlur={commitRadius}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        commitRadius();
                                    }
                                }}
                            />
                            <p className="text-xs text-muted-foreground">
                                Type a radius (Enter), or draw/drag the circle
                                on the map
                            </p>
                        </div>
                    )}

                    <div className="h-[460px]">
                        <LocationMap
                            ref={mapRef}
                            geometryType={geometryType}
                            initial={location}
                            onGeometryChange={setGeometry}
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} disabled={busy}>
                        Cancel
                    </Button>
                    <Button onClick={save} disabled={!canSave} loading={busy}>
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
